import { useEffect, useState, useRef } from "react";
import { api, getToken, withDlToken, formatApiErrorDetail } from "@/lib/api";
import NdaForm from "@/components/NdaForm";
import SendEmailDialog from "@/components/SendEmailDialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, FileText, Download, Send, Upload, Trash2, Pencil, Lock, Unlock, Clock, FileSignature, ShieldCheck, Eye, Mail, Link2Off } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Brouillon", "Envoyé", "Consulté", "Signé", "Refusé", "Expiré"];
const STATUS_STYLES = {
  Brouillon: "border-slate-200 text-slate-600",
  "Envoyé": "bg-blue-50 text-blue-700 border-blue-200",
  "Consulté": "bg-amber-50 text-amber-700 border-amber-200",
  "Signé": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Refusé": "bg-red-50 text-red-700 border-red-200",
  "Expiré": "bg-slate-100 text-slate-500 border-slate-300",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

function StatusBadge({ status }) {
  return <Badge variant="outline" className={`rounded-full font-medium ${STATUS_STYLES[status] || ""}`}>{status}</Badge>;
}

export default function Ndas() {
  const [ndas, setNdas] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [oppDetail, setOppDetail] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("Aperçu PDF");
  const [msStatus, setMsStatus] = useState({ configured: false, connected: false, email: null });
  const [emailNda, setEmailNda] = useState(null);
  const uploadRef = useRef(null);

  const load = () => api.get("/ndas").then((r) => setNdas(r.data));
  const loadMsStatus = () => api.get("/microsoft/status").then((r) => setMsStatus(r.data)).catch(() => {});
  useEffect(() => { load(); loadMsStatus(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ms = params.get("ms");
    if (ms === "connected") {
      toast.success("Compte Microsoft connecté");
      loadMsStatus();
      window.history.replaceState({}, "", "/nda");
    } else if (ms === "error") {
      toast.error("La connexion Microsoft a échoué. Réessayez.");
      window.history.replaceState({}, "", "/nda");
    }
  }, []);

  const connectMicrosoft = () => {
    const base = encodeURIComponent(window.location.origin);
    window.location.href = `${window.location.origin}/api/microsoft/login?auth=${getToken()}&redirect_base=${base}`;
  };
  const disconnectMicrosoft = async () => {
    await api.post("/microsoft/disconnect");
    toast.success("Compte Microsoft déconnecté");
    loadMsStatus();
  };

  const filtered = ndas.filter(
    (n) =>
      (statusFilter === "all" || n.status === statusFilter) &&
      (`${n.first_name} ${n.last_name} ${n.company} ${n.opportunity_title} ${n.email}`
        .toLowerCase()
        .includes(q.toLowerCase()))
  );

  const openTemplate = async () => {
    const r = await api.get("/nda-template");
    setTpl(r.data.content);
    setTplOpen(true);
  };
  const saveTemplate = async () => {
    await api.put("/nda-template", { content: tpl });
    toast.success("Modèle de NDA enregistré");
    setTplOpen(false);
  };

  const openDetail = async (n) => {
    setSelected(n);
    setOppDetail(null);
    if (n.opportunity_id) {
      try {
        const r = await api.get(`/opportunities/${n.opportunity_id}`);
        setOppDetail(r.data);
      } catch (e) { console.debug("opportunity fetch failed (may be gone):", e?.message || e); }
    }
  };

  const refreshSelected = async (id) => {
    const r = await api.get(`/ndas/${id}`);
    setSelected(r.data);
    load();
  };

  const downloadPdf = (n) => {
    const a = document.createElement("a");
    a.href = withDlToken(`${API}/ndas/${n.id}/pdf`);
    a.download = `NDA-${n.first_name}-${n.last_name}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const previewNda = (n) => {
    setPreviewTitle(`Aperçu — ${n.first_name} ${n.last_name}`);
    setPreviewUrl(withDlToken(`${API}/ndas/${n.id}/pdf`));
    setPreviewOpen(true);
  };

  const previewTemplate = async () => {
    try {
      const res = await fetch(`${API}/nda-template/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ content: tpl }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setPreviewTitle("Aperçu du modèle (données d'exemple)");
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewOpen(true);
    } catch {
      toast.error("Impossible de générer l'aperçu");
    }
  };

  const sendNda = (n) => {
    setEmailNda(n);
  };

  const changeStatus = async (n, status) => {
    await api.put(`/ndas/${n.id}`, { status });
    toast.success(`Statut → ${status}`);
    refreshSelected(n.id);
  };

  const uploadSigned = async (n, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/ndas/${n.id}/signed-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Version signée téléversée");
      refreshSelected(n.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteSigned = async (n) => {
    await api.delete(`/ndas/${n.id}/signed`);
    toast.success("Document signé supprimé");
    refreshSelected(n.id);
  };

  const confirmDelete = async () => {
    await api.delete(`/ndas/${deleting.id}`);
    toast.success("NDA supprimé");
    setDeleting(null);
    setSelected(null);
    load();
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="ndas-page">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="overline text-[#C6A87C]">Confidentialité</p>
          <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">NDA</h1>
          <p className="mt-2 text-slate-500">{ndas.length} accords de confidentialité suivis.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {msStatus.configured && (
            msStatus.connected ? (
              <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700" data-testid="ms-status-chip">
                <ShieldCheck className="h-3.5 w-3.5" /> Outlook · {msStatus.email || "connecté"}
                <button onClick={disconnectMicrosoft} title="Déconnecter" data-testid="ms-disconnect-btn" className="ml-1 rounded-full p-0.5 hover:text-emerald-900"><Link2Off className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <Button variant="outline" onClick={connectMicrosoft} data-testid="ms-connect-header-btn" className="rounded-full">
                <Mail className="mr-2 h-4 w-4" /> Connecter Microsoft
              </Button>
            )
          )}
          <Button variant="outline" onClick={openTemplate} data-testid="nda-template-btn" className="rounded-full">
            <FileText className="mr-2 h-4 w-4" /> Modèle
          </Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-nda-btn" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
            <Plus className="mr-2 h-4 w-4" /> Nouveau NDA
          </Button>
        </div>
      </header>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input data-testid="nda-search" placeholder="Rechercher un destinataire, une société, une opportunité…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 lg:w-96" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 w-full sm:w-48" data-testid="nda-filter-status"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`nda-filter-${s}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
              <TableHead className="text-slate-500">Destinataire</TableHead>
              <TableHead className="text-slate-500">Opportunité</TableHead>
              <TableHead className="text-slate-500">Statut</TableHead>
              <TableHead className="text-slate-500">Envoyé le</TableHead>
              <TableHead className="text-slate-500">Signé le</TableHead>
              <TableHead className="text-right text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((n) => (
              <TableRow key={n.id} data-testid={`nda-row-${n.id}`} className="cursor-pointer hover:bg-slate-50/60" onClick={() => openDetail(n)}>
                <TableCell>
                  <p className="font-medium text-[#0F172A]">{n.first_name} {n.last_name}</p>
                  <p className="text-xs text-slate-400">{n.company || n.email}</p>
                </TableCell>
                <TableCell className="text-slate-600">{n.opportunity_title || "—"}</TableCell>
                <TableCell>
                  <StatusBadge status={n.status} />
                  {n.open_count > 0 && (
                    <span className="mt-1 flex items-center gap-1 text-xs text-[#C6A87C]" data-testid={`nda-opens-${n.id}`}>
                      <Eye className="h-3 w-3" /> {n.open_count} ouverture{n.open_count > 1 ? "s" : ""}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-slate-500">{fmtDate(n.sent_date)}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(n.signed_date)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <button title="Aperçu PDF" data-testid={`nda-preview-${n.id}`} onClick={() => previewNda(n)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]"><Eye className="h-4 w-4" /></button>
                    <button title="Télécharger le PDF" data-testid={`nda-pdf-${n.id}`} onClick={() => downloadPdf(n)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]"><Download className="h-4 w-4" /></button>
                    <button title="Envoyer par email" data-testid={`nda-send-${n.id}`} onClick={() => sendNda(n)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]"><Send className="h-4 w-4" /></button>
                    <button title="Modifier" data-testid={`nda-edit-${n.id}`} onClick={() => { setEditing(n); setFormOpen(true); }} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]"><Pencil className="h-4 w-4" /></button>
                    <button title="Supprimer" data-testid={`nda-delete-${n.id}`} onClick={() => setDeleting(n)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <p className="py-16 text-center text-slate-400">Aucun NDA. Créez-en un ou envoyez-le depuis une opportunité.</p>}
      </div>

      <NdaForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} />

      <SendEmailDialog
        open={!!emailNda}
        onOpenChange={(v) => !v && setEmailNda(null)}
        nda={emailNda}
        msStatus={msStatus}
        onConnect={connectMicrosoft}
        onSent={(id) => { load(); if (selected?.id === id) refreshSelected(id); }}
      />

      {/* PDF preview */}
      <Dialog open={previewOpen} onOpenChange={(v) => { setPreviewOpen(v); if (!v && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl); }}>
        <DialogContent className="max-w-4xl" data-testid="nda-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0F172A]">{previewTitle}</DialogTitle>
            <DialogDescription className="text-slate-500">Rendu fidèle au PDF exporté.</DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <iframe title="Aperçu PDF" src={previewUrl} data-testid="nda-preview-frame" className="h-[70vh] w-full rounded-lg border border-slate-200" />
          )}
        </DialogContent>
      </Dialog>


      {/* Detail */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="nda-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#0F172A]">{selected.first_name} {selected.last_name}</DialogTitle>
                <DialogDescription className="text-slate-500">{selected.company} · {selected.opportunity_title}</DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={selected.status} />
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected, v)}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="nda-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`nda-status-opt-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-slate-50 p-3"><p className="overline text-slate-400">Email</p>{selected.email || "—"}</div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="overline text-slate-400">Téléphone</p>{selected.phone || "—"}</div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="overline text-slate-400">Durée</p>{selected.confidentiality_duration}</div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="overline text-slate-400">Juridiction</p>{selected.jurisdiction}</div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="overline text-slate-400">Envoyé le</p>{fmtDate(selected.sent_date)}</div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="overline text-slate-400">Signé le</p>{fmtDate(selected.signed_date)}</div>
              </div>

              {/* Email open tracking */}
              <div className="rounded-xl border border-slate-200 p-4" data-testid="nda-tracking">
                <p className="overline mb-2 flex items-center gap-1.5 text-slate-400"><Eye className="h-3.5 w-3.5" /> Suivi d'ouverture de l'email</p>
                {selected.open_count > 0 ? (
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><p className="text-xs text-slate-400">Ouvertures</p><p className="font-semibold text-[#C6A87C]" data-testid="nda-open-count">{selected.open_count}</p></div>
                    <div><p className="text-xs text-slate-400">1ère ouverture</p><p className="font-medium text-slate-700" data-testid="nda-first-open">{fmtDateTime(selected.first_opened_at)}</p></div>
                    <div><p className="text-xs text-slate-400">Dernière</p><p className="font-medium text-slate-700" data-testid="nda-last-open">{fmtDateTime(selected.last_opened_at)}</p></div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Pas encore consulté par le destinataire. Le statut passera automatiquement à « Consulté » à la première ouverture.</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="rounded-full" data-testid="nda-detail-preview" onClick={() => previewNda(selected)}><Eye className="mr-1.5 h-4 w-4" /> Aperçu PDF</Button>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="nda-detail-pdf" onClick={() => downloadPdf(selected)}><Download className="mr-1.5 h-4 w-4" /> Télécharger le PDF</Button>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="nda-detail-send" onClick={() => sendNda(selected)}><Send className="mr-1.5 h-4 w-4" /> Envoyer par email</Button>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="nda-detail-upload" onClick={() => uploadRef.current?.click()}><Upload className="mr-1.5 h-4 w-4" /> Téléverser version signée</Button>
                <input ref={uploadRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="nda-signed-input" onChange={(e) => { if (e.target.files?.[0]) uploadSigned(selected, e.target.files[0]); e.target.value = ""; }} />
                {selected.signed_document_path && (
                  <>
                    <a href={withDlToken(`${API}/files/${selected.signed_document_path}`)} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border px-3 py-1.5 text-sm text-slate-600 hover:border-[#C6A87C]" data-testid="nda-view-signed"><FileSignature className="mr-1.5 h-4 w-4" /> Voir le signé</a>
                    <Button variant="outline" size="sm" className="rounded-full text-red-600" data-testid="nda-delete-signed" onClick={() => deleteSigned(selected)}><Trash2 className="mr-1.5 h-4 w-4" /> Supprimer le signé</Button>
                  </>
                )}
              </div>

              {/* Teaser / confidential access */}
              <div className={`rounded-xl border p-4 ${selected.status === "Signé" ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`} data-testid="nda-access-block">
                <p className="flex items-center gap-2 font-semibold text-[#0F172A]">
                  {selected.status === "Signé" ? <Unlock className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4 text-slate-400" />}
                  {selected.status === "Signé" ? "Documents confidentiels débloqués" : "Accès limité (teaser)"}
                </p>
                {selected.status === "Signé" ? (
                  <div className="mt-2 space-y-1.5" data-testid="nda-confidential-docs">
                    {(oppDetail?.documents || []).length === 0 && <p className="text-sm text-slate-500">Aucun document confidentiel enregistré sur cette opportunité.</p>}
                    {(oppDetail?.documents || []).map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-[#C6A87C]" /> {d.name} <span className="text-xs text-slate-400">{d.size}</span></div>
                    ))}
                    <p className="flex items-center gap-1.5 pt-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Le destinataire peut désormais accéder aux documents autorisés.</p>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-slate-500">Tant que le NDA n'est pas signé, seul un aperçu limité de l'opportunité « {selected.opportunity_title} » est accessible. Les documents confidentiels restent verrouillés.</p>
                )}
              </div>

              {/* History */}
              <div>
                <p className="overline mb-2 text-slate-400">Historique</p>
                <div className="space-y-2">
                  {(selected.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex gap-3 text-sm" data-testid={`nda-history-${i}`}>
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A87C]" />
                      <div>
                        <p className="text-xs text-slate-400">{fmtDate(h.date)}</p>
                        <p className="text-slate-700">{h.action}{h.note ? ` — ${h.note}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Template editor */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="nda-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0F172A]">Modèle de NDA standard</DialogTitle>
            <DialogDescription className="text-slate-500">Modifiable. Utilisez les variables : {"{{destinataire}}, {{societe}}, {{adresse}}, {{email}}, {{telephone}}, {{opportunite}}, {{duree}}, {{juridiction}}, {{date_envoi}}"}</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="nda-template-textarea" value={tpl} onChange={(e) => setTpl(e.target.value)} rows={18} className="font-mono text-xs" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTplOpen(false)}>Annuler</Button>
            <Button variant="outline" className="rounded-full" data-testid="nda-template-preview" onClick={previewTemplate}><Eye className="mr-1.5 h-4 w-4" /> Aperçu PDF</Button>
            <Button className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="nda-template-save" onClick={saveTemplate}>Enregistrer le modèle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="nda-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Supprimer le NDA ?</AlertDialogTitle>
            <AlertDialogDescription>Le NDA de « {deleting?.first_name} {deleting?.last_name} » sera supprimé définitivement.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="nda-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="nda-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
